import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { DateProvider } from '@mimir/common/util/dateProvider';
import type { Logger } from '@mimir/common/types/logger';
import { ExternalSmTransferMonitorImpl } from '@mimir/client-common/fileSync/externalSmTransferMonitorImpl';
import type { ExternalSmTransferMonitor } from '@mimir/client-common/fileSync/externalSmTransferMonitor';
import { waitForLocallyAvailableFile } from '@mimir/client-common/fileSync/waitForLocallyAvailableFile';
import { getTransferRequests } from '../util/api';

const POLL_DURATION_MS = 5 * 1000;

/**
 * Shared ExternalSmTransferMonitor instances keyed by storageId.
 * Using a single instance per storage ensures that all pending transfers
 * share one polling interval rather than each creating their own.
 */
const monitorsByStorageId = new Map<string, ExternalSmTransferMonitor>();

/**
 * Returns the shared ExternalSmTransferMonitor for the given storageId,
 * creating one on first call. The applicationContext is captured at
 * creation time to provide the waitForFile implementation.
 */
export function getExternalSmTransferMonitor(
  storageId: string,
  applicationContext: AdobePanelContext | ElectronContext,
  logger: Logger,
  dateProvider: DateProvider,
): ExternalSmTransferMonitor {
  const existing = monitorsByStorageId.get(storageId);
  if (existing !== undefined) {
    return existing;
  }

  const monitor = new ExternalSmTransferMonitorImpl(
    logger,
    POLL_DURATION_MS,
    async () => {
      const transfers = await getTransferRequests(storageId, true);
      return transfers.map(t => t.id);
    },
    (candidatePaths, reporters) => waitForLocallyAvailableFile(applicationContext, logger, dateProvider, candidatePaths, reporters),
    (callback, ms) => window.setInterval(callback, ms),
    (handle) => window.clearInterval(handle),
  );
  monitorsByStorageId.set(storageId, monitor);
  return monitor;
}
